import React from "react";

function clean(value) {
  return String(value ?? "").trim();
}

export default function TextbookWorkspaceNavigator({
  currentWorkspace = "overview",
  onNavigate,
  bookMapEnabled = false,
  chapterWorkspaceEnabled = false,
  projectPlan = null,
  canManageProject = true,
  isFR = false,
}) {
  const items = Array.isArray(projectPlan?.bookMap?.items)
    ? projectPlan.bookMap.items
    : [];

  const chapters = items.filter(
    (item) => clean(item?.type) === "chapter"
  ).length;

  const frontMatter = items.filter(
    (item) =>
      clean(item?.type) === "front_matter" &&
      !clean(item?.parentItemId)
  ).length;

  const backMatter = items.filter(
    (item) =>
      clean(item?.type) === "back_matter" &&
      !clean(item?.parentItemId)
  ).length;

  const overview = projectPlan?.overview || {};
  const projectChecks = [
    overview.subjectOrDiscipline,
    overview.intendedLearners,
    overview.educationalLevel,
    overview.editionOrVersion,
  ];

  const projectCompleted = projectChecks.filter(
    (value) => clean(value)
  ).length;

  const setupReady =
    projectCompleted === projectChecks.length &&
    chapters > 0;

  const ownerWorkspaces = [
    {
      id: "overview",
      label: isFR ? "Préparer" : "Setup",
      status: setupReady
        ? (isFR ? "Prêt" : "Ready")
        : (isFR ? "À compléter" : "Setup"),
      disabled: !bookMapEnabled,
    },
    {
      id: "book-map",
      label: isFR ? "Structure du livre" : "Book Structure",
      status: isFR
        ? `${frontMatter} avant · ${backMatter} fin`
        : `Front ${frontMatter} · Back ${backMatter}`,
      disabled: !bookMapEnabled,
    },
    {
      id: "write-chapters",
      label: isFR ? "Écrire" : "Write",
      status: chapters > 0
        ? (isFR ? `${chapters} chapitre(s)` : `${chapters} chapter(s)`)
        : (isFR ? "En attente" : "Waiting"),
      disabled:
        !chapterWorkspaceEnabled ||
        chapters < 1,
    },
    {
      id: "review-your-book",
      label: isFR ? "Réviser" : "Review",
      status:
        chapterWorkspaceEnabled && chapters > 0
          ? (isFR ? "Disponible" : "Available")
          : (isFR ? "En attente" : "Waiting"),
      disabled:
        !chapterWorkspaceEnabled ||
        chapters < 1,
    },
    {
      id: "publication-proof",
      label:
        isFR
          ? "Épreuve de publication"
          : "Publication Proof",
      status:
        chapterWorkspaceEnabled && chapters > 0
          ? (isFR ? "Disponible" : "Available")
          : (isFR ? "En attente" : "Waiting"),
      disabled:
        !chapterWorkspaceEnabled ||
        chapters < 1,
    },
    {
      id: "publication-preparation",
      label:
        isFR
          ? "Préparer la publication"
          : "Publication Preparation",
      status:
        chapterWorkspaceEnabled && chapters > 0
          ? (isFR ? "Disponible" : "Available")
          : (isFR ? "En attente" : "Waiting"),
      disabled:
        !chapterWorkspaceEnabled ||
        chapters < 1,
    },
  ];

  const contributorWorkspaces = [
    {
      id: "write-chapters",
      label:
        isFR
          ? "Contribuer"
          : "Contribute",
      status:
        chapters > 0
          ? (
              isFR
                ? `${chapters} chapitre(s)`
                : `${chapters} chapter(s)`
            )
          : (
              isFR
                ? "En attente"
                : "Waiting"
            ),
      disabled:
        !chapterWorkspaceEnabled ||
        chapters < 1,
    },
  ];

  const workspaces =
    canManageProject
      ? ownerWorkspaces
      : contributorWorkspaces;

  return (
    <nav
      data-textbook-workbar="true"
      data-textbook-studio-gateway="true"
      aria-label={
        isFR
          ? "Navigation du studio de manuel"
          : "Textbook Studio navigation"
      }
      className="ns-textbook-studio-gateway"
    >
      <div className="ns-textbook-studio-gateway-head">
        <div className="ns-textbook-studio-gateway-mark" aria-hidden="true">
          <span>NS</span>
        </div>

        <div className="ns-textbook-studio-gateway-copy">
          <div className="ns-textbook-studio-gateway-kicker">
            {canManageProject
              ? (
                  isFR
                    ? "STUDIO MANUEL"
                    : "TEXTBOOK STUDIO"
                )
              : (
                  isFR
                    ? "ESPACE CONTRIBUTEUR"
                    : "CONTRIBUTOR WORKSPACE"
                )}
          </div>

          <div className="ns-textbook-studio-gateway-title">
            {canManageProject
              ? (
                  isFR
                    ? "Construisez le livre complet depuis une seule porte"
                    : "Build the complete book from one doorway"
                )
              : (
                  isFR
                    ? "Contribuez uniquement aux chapitres ouverts par le propriétaire"
                    : "Contribute only to chapters opened by the Owner"
                )}
          </div>

          <div className="ns-textbook-studio-gateway-note">
            {canManageProject
              ? (
                  isFR
                    ? "Pages liminaires · Manuscrit principal · Éléments de fin"
                    : "Front Matter · Main Manuscript · Back Matter"
                )
              : (
                  isFR
                    ? "Lire le Canon · Écrire une proposition · Soumettre au propriétaire"
                    : "Read Canon · Write a Proposal · Submit for Owner review"
                )}
          </div>
        </div>

        {canManageProject ? (
          <div className="ns-textbook-studio-composition" aria-label={
            isFR ? "Composition du livre" : "Book composition"
          }>
            <span>
              <strong>{frontMatter}</strong>
              {isFR ? " Avant" : " Front"}
            </span>
            <span>
              <strong>{chapters}</strong>
              {isFR ? " Chap." : " Chapters"}
            </span>
            <span>
              <strong>{backMatter}</strong>
              {isFR ? " Fin" : " Back"}
            </span>
          </div>
        ) : null}
      </div>

      <div className="ns-textbook-studio-gateway-actions">
        {workspaces.map((workspace) => {
          const active =
            currentWorkspace === workspace.id;

          return (
            <button
              key={workspace.id}
              type="button"
              disabled={workspace.disabled}
              aria-current={
                active ? "page" : undefined
              }
              onClick={() => {
                if (
                  workspace.disabled ||
                  typeof onNavigate !== "function"
                ) {
                  return;
                }

                onNavigate(workspace.id);
              }}
              className={
                "ns-textbook-studio-gateway-action " +
                (active ? "is-active" : "")
              }
            >
              <span className="ns-textbook-studio-gateway-action-label">
                {workspace.label}
              </span>
              <span className="ns-textbook-studio-gateway-action-status">
                {workspace.status}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
